
import { Component, ProvideReactive, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { ContextColorProvider } from '@warrenbrasil/nebraska-web';
import { HomeTemplate } from '@/modules/home/components/HomeTemplate';
import { HomeIllustration } from '@/modules/home/components/HomeIllustration';
import { LegacyHomeBalanceSummary } from '@/modules/home/components/LegacyHomeBalanceSummary';
import { HomeNews } from '@/modules/home/components/HomeNews';
import { HomeWidgets } from '@/modules/home/components/HomeWidgets';
import { HomeDashStatus } from '@/modules/home/components/HomeDashStatus';
import { HomeCash } from '@/modules/home/components/HomeCash';
import { ModalSuggestions as PensionModalSuggestions } from '@/modules/pension/components/ModalSuggestions';
import { clearNextRoutes } from '@/modules/common/store/RegulatoryUpdateRoutesOrchestrator';
import { UIModule } from '@/modules/common/store/UIModule';
import { isWarrenManaged } from '@/modules/common/services/partner';
import {
  HomeHeroDocument,
  HomeHeroQuery,
  HomeDashStatusQuery,
  HomeDashStatusDocument,
  GlobalSummaryBalance,
  DashStatus,
  GlobalSummaryHeaderImageSize,
  DashStatusCardImagePlatform,
  DashStatusCard
} from '@/modules/common/services/graphql/server/server.sdk';
import { WarrenEqualsModal } from '@/modules/home/components/WarrenEqualsModal';
import * as trackEvents from './track-events';

import { HomeCES } from '@/modules/home/components/HomeCES';

import { getBalances } from '@/modules/home/services/bff/bff';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import { BalancesResponse } from '../../services/bff/types';
import HomeBalanceSummary from '../../components/HomeBalanceSummary/HomeBalanceSummary.vue';

const CustomerModule = namespace('CustomerModule');

@Component({
  components: {
    ContextColorProvider,
    HomeTemplate,
    HomeIllustration,
    LegacyHomeBalanceSummary,
    HomeNews,
    HomeWidgets,
    HomeDashStatus,
    HomeCash,
    PensionModalSuggestions,
    WarrenEqualsModal,
    HomeCES,
    HomeBalanceSummary
  }
})
export default class Home extends Vue {
  dashStatus: Reactive<DashStatus> = null;
  balances: Reactive<GlobalSummaryBalance> = null;
  balanceDetails: Reactive<BalancesResponse> = null;
  images: Reactive<GlobalSummaryHeaderImageSize | DashStatusCardImagePlatform> =
    null;

  isLoading = false;
  hasError = false;
  showHomeCashDepositModal = false;

  @CustomerModule.Getter('isRegulatoryStatusBlocked')
  readonly isOverdue!: boolean;

  @ProvideReactive()
  get shouldTriggerHomeAnimations() {
    return !UIModule.getIsOpenWelcomeCurtain;
  }

  created() {
    this.load();
  }

  mounted() {
    trackEvents.trackHomePageView();
    clearNextRoutes();
  }

  reloadBalanceSummary() {
    this.load();
    trackEvents.trackReloadWorthErrorClick();
  }

  public openHomeCashDepositModal() {
    this.showHomeCashDepositModal = true;
  }

  public closeHomeCashDepositModal() {
    this.showHomeCashDepositModal = false;
  }

  async load() {
    this.isLoading = true;
    this.hasError = false;

    try {
      await this.loadDashStatus();

      if (!this.dashStatus) {
        await this.loadBalanceSummary();
      }
    } catch {
      this.hasError = true;
      trackEvents.trackWorthErrorLoad();
    } finally {
      this.isLoading = false;
    }
  }

  private async loadDashStatus() {
    const { data } = await this.$apollo.query<HomeDashStatusQuery>({
      query: HomeDashStatusDocument
    });

    this.dashStatus = data.globalSummary!.dashStatus!;

    if (this.dashStatus?.card?.image?.web) {
      this.images = this.dashStatus.card.image.web;
    }
  }

  get isUnifiedAccountEnabled() {
    return FeatureFlagsModule.isEnabled(FeatureFlags.UNIFIED_ACCOUNT);
  }

  private async loadBalanceSummary() {
    this.isUnifiedAccountEnabled
      ? await this.loadHeroBalance()
      : await this.loadLegacyHeroBalance();
  }

  private async loadHeroBalance() {
    this.balanceDetails = await getBalances();
    this.images = this.balanceDetails.headers.images;
  }

  private async loadLegacyHeroBalance() {
    const { data } = await this.$apollo.query<HomeHeroQuery>({
      query: HomeHeroDocument,
      variables: {
        dateFormat: 'DD/MM/YYYY',
        includeHeroImage: isWarrenManaged()
      }
    });
    this.balances = data.globalSummary?.balances!;
    this.images = data.globalSummary?.header?.images?.web!;
  }

  handleDashButtonsEvents(dashStatusCard: DashStatusCard) {
    return trackEvents.trackDashStatusButtonClick(dashStatusCard);
  }
}
